/**
 * Приглашение. Макет data/Приглашение.dc.html.
 *
 * §4.4 задаёт шесть взаимоисключающих состояний одного токена: действительно,
 * принято, отклонено, отозвано, истекло, некорректно. Каждое — своё сообщение
 * и свой набор действий; общего «что-то пошло не так» здесь быть не может.
 *
 * Новому пользователю принятие создаёт учётную запись, существующему —
 * только добавляет доступ, без дубликата (§4.4). Отдельной регистрации
 * в системе нет: учётная запись появляется только по приглашению.
 * Существующий пользователь подтверждает приём своим паролем.
 */

import { ErrorCode } from "@taskflow/contracts";
import { CheckCircle2, Clock, ShieldOff, XCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppDispatch } from "@/app/hooks";
import {
  getWsClient,
  sessionEstablished,
  setAccessToken,
  type WsError,
} from "@taskflow/client-core";
import { Button, ErrorState, FormField, Input, PasswordInput, SkeletonRows } from "@/shared/ui";

import {
  type InvitationPreview,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
  useInvitationQuery,
} from "./api";

export function InvitePage() {
  const { token = "" } = useParams<{ token: string }>();
  const { data, isLoading, error, refetch } = useInvitationQuery(token, { skip: token === "" });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-5">
      <div className="w-full max-w-[440px] rounded-lg border border-line bg-surface p-8 shadow-sm max-sm:p-5">
        <h1 className="mb-5 text-[19px] font-extrabold tracking-tight text-fg">
          mytaskflow
        </h1>

        {isLoading && <SkeletonRows rows={4} />}

        {error !== undefined && !isLoading && <InvalidToken error={error as WsError} onRetry={() => void refetch()} />}

        {data !== undefined && <InvitationBody token={token} invitation={data} />}
      </div>
    </div>
  );
}

/**
 * Некорректный токен. §4.4 не различает «нет такого» и «подделка»:
 * ответ одинаковый, чтобы перебор токенов ничего не давал.
 */
function InvalidToken({ error, onRetry }: { error: WsError; onRetry: () => void }) {
  if (error.data.code === ErrorCode.NOT_FOUND) {
    return (
      <Outcome
        icon={XCircle}
        tone="danger"
        title="Приглашение недействительно"
        description="Ссылка неверна или больше не существует. Попросите отправить новое приглашение."
      />
    );
  }

  return <ErrorState message={error.data.message} correlationId={error.data.correlationId} onRetry={onRetry} />;
}

function InvitationBody({
  token,
  invitation,
}: {
  token: string;
  invitation: InvitationPreview;
}) {
  const navigate = useNavigate();
  const [justAccepted, setJustAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);

  if (justAccepted) {
    return (
      <Outcome
        icon={CheckCircle2}
        tone="success"
        title="Приглашение принято"
        description={
          invitation.boardName === null
            ? "Доступ выдан. Можно приступать к работе."
            : `Доступ к доске «${invitation.boardName}» выдан.`
        }
        action={<Button block onClick={() => void navigate("/tasks", { replace: true })}>Перейти к задачам</Button>}
      />
    );
  }

  if (declined) {
    return (
      <Outcome
        icon={XCircle}
        tone="neutral"
        title="Приглашение отклонено"
        description="Если это ошибка, попросите отправить новое приглашение — прежняя ссылка больше не действует."
      />
    );
  }

  switch (invitation.status) {
    case "ACCEPTED":
      return (
        <Outcome
          icon={CheckCircle2}
          tone="success"
          title="Приглашение уже принято"
          description="Доступ выдан ранее. Войдите под своей учётной записью."
          action={<Button block onClick={() => void navigate("/login")}>Перейти ко входу</Button>}
        />
      );

    case "DECLINED":
      return (
        <Outcome
          icon={XCircle}
          tone="neutral"
          title="Приглашение отклонено"
          description="Эта ссылка больше не действует. Для доступа нужно новое приглашение."
        />
      );

    case "REVOKED":
      return (
        <Outcome
          icon={ShieldOff}
          tone="danger"
          title="Приглашение отозвано"
          description="Администратор отозвал приглашение. Обратитесь к нему, если считаете это ошибкой."
        />
      );

    case "EXPIRED":
      return (
        <Outcome
          icon={Clock}
          tone="warning"
          title="Срок приглашения истёк"
          // §4.4: истёкшее можно отправить повторно, но это делает приглашающий.
          description="Ссылка действовала ограниченное время. Попросите отправить приглашение повторно."
        />
      );

    case "PENDING":
      return (
        <AcceptForm
          token={token}
          invitation={invitation}
          onAccepted={() => setJustAccepted(true)}
          onDeclined={() => setDeclined(true)}
        />
      );
  }
}

const MIN_PASSWORD_LENGTH = 12;

type ProfileField = "lastName" | "firstName" | "password" | "passwordRepeat";

function AcceptForm({
  token,
  invitation,
  onAccepted,
  onDeclined,
}: {
  token: string;
  invitation: InvitationPreview;
  onAccepted: () => void;
  onDeclined: () => void;
}) {
  const dispatch = useAppDispatch();
  const [accept, { isLoading: accepting }] = useAcceptInvitationMutation();
  const [decline, { isLoading: declining }] = useDeclineInvitationMutation();

  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [position, setPosition] = useState("");
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<ProfileField, string>>>({});
  const [error, setError] = useState<string | null>(null);

  // Адреса в базе нет — человек заводит учётную запись прямо здесь.
  // Отдельной регистрации в системе нет: только по приглашению.
  const needsProfile = invitation.requiresRegistration;

  const validate = (): Partial<Record<ProfileField, string>> => {
    const problems: Partial<Record<ProfileField, string>> = {};
    if (password === "") problems.password = "Обязательное поле";
    if (!needsProfile) return problems;
    if (lastName.trim() === "") problems.lastName = "Обязательное поле";
    if (firstName.trim() === "") problems.firstName = "Обязательное поле";
    if (password !== "" && password.length < MIN_PASSWORD_LENGTH) {
      problems.password = `Не короче ${String(MIN_PASSWORD_LENGTH)} символов`;
    }
    if (passwordRepeat !== password) problems.passwordRepeat = "Пароли не совпадают";
    return problems;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (accepting) return;
    setError(null);
    const problems = validate();
    setFieldErrors(problems);
    if (Object.keys(problems).length > 0) return;

    try {
      const session = await accept({
        token,
        password,
        ...(needsProfile
          ? {
              lastName: lastName.trim(),
              firstName: firstName.trim(),
              ...(middleName.trim() === "" ? {} : { middleName: middleName.trim() }),
              ...(position.trim() === "" ? {} : { position: position.trim() }),
            }
          : {}),
      }).unwrap();

      setAccessToken(session.accessToken);
      dispatch(sessionEstablished({ user: session.user, permissions: session.permissions }));
      getWsClient().connect();
      onAccepted();
    } catch (raw) {
      const wsError = raw as WsError;
      const serverFields = wsError.data.fieldErrors ?? {};
      setFieldErrors({
        ...(serverFields.lastName ? { lastName: serverFields.lastName } : {}),
        ...(serverFields.firstName ? { firstName: serverFields.firstName } : {}),
        ...(serverFields.password ? { password: serverFields.password } : {}),
      });
      setError(
        wsError.data.code === ErrorCode.INVITATION_EXPIRED
          ? "Срок приглашения истёк, пока страница была открыта"
          : wsError.data.code === ErrorCode.AUTH_REQUIRED
            ? "Неверный пароль"
            : wsError.data.message,
      );
    }
  };

  const handleDecline = async () => {
    try {
      await decline({ token }).unwrap();
      onDeclined();
    } catch (raw) {
      setError((raw as WsError).data.message);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <h2 className="text-xl font-extrabold text-fg">
        {invitation.boardName === null ? "Приглашение в систему" : "Приглашение в доску"}
      </h2>
      <p className="mt-1 text-[13px] text-muted">
        {invitation.inviterName === null
          ? "Вас пригласили в mytaskflow."
          : `${invitation.inviterName} приглашает вас в mytaskflow.`}
      </p>

      <dl className="mt-4 flex flex-col gap-2 rounded-md bg-sunken px-3.5 py-3 text-[13px]">
        <Detail label="Почта" value={invitation.email} />
        <Detail label="Подразделение" value={invitation.departmentName} />
        <Detail label="Доска" value={invitation.boardName} />
        <Detail label="Проект" value={invitation.projectName} />
        <Detail label="Роль" value={invitation.roleName} />
        <Detail label="Действует до" value={invitation.expiresAtLabel} />
      </dl>

      {needsProfile ? (
        <div className="mt-4 flex flex-col gap-3.5">
          <p className="text-[13px] text-muted">
            Учётной записи с этой почтой ещё нет. Заполните данные — учётная запись будет создана,
            и вы сразу получите доступ.
          </p>
          <FormField label="Фамилия" required error={fieldErrors.lastName ?? null}>
            {(props) => (
              <Input
                {...props}
                value={lastName}
                autoComplete="family-name"
                maxLength={128}
                onChange={(event) => setLastName(event.target.value)}
              />
            )}
          </FormField>
          <FormField label="Имя" required error={fieldErrors.firstName ?? null}>
            {(props) => (
              <Input
                {...props}
                value={firstName}
                autoComplete="given-name"
                maxLength={128}
                onChange={(event) => setFirstName(event.target.value)}
              />
            )}
          </FormField>
          <FormField label="Отчество">
            {(props) => (
              <Input
                {...props}
                value={middleName}
                autoComplete="additional-name"
                maxLength={128}
                onChange={(event) => setMiddleName(event.target.value)}
              />
            )}
          </FormField>
          <FormField label="Должность">
            {(props) => (
              <Input
                {...props}
                value={position}
                autoComplete="organization-title"
                maxLength={255}
                onChange={(event) => setPosition(event.target.value)}
              />
            )}
          </FormField>
          <FormField
            label="Пароль"
            required
            hint={`Не короче ${String(MIN_PASSWORD_LENGTH)} символов`}
            error={fieldErrors.password ?? null}
          >
            {(props) => (
              <PasswordInput
                {...props}
                value={password}
                autoComplete="new-password"
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </FormField>
          <FormField label="Повторите пароль" required error={fieldErrors.passwordRepeat ?? null}>
            {(props) => (
              <PasswordInput
                {...props}
                value={passwordRepeat}
                autoComplete="new-password"
                onChange={(event) => setPasswordRepeat(event.target.value)}
              />
            )}
          </FormField>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3.5">
          {/* Ссылка из письма сама по себе вход не даёт: учётная запись уже есть,
              и приём подтверждается её паролем. */}
          <p className="text-[13px] text-muted">
            Учётная запись с этой почтой уже есть. Введите её пароль, чтобы принять приглашение.
          </p>
          <FormField label="Пароль" required error={fieldErrors.password ?? null}>
            {(props) => (
              <PasswordInput
                {...props}
                value={password}
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </FormField>
          <Link to="/login" className="self-start text-[13px] font-semibold text-accent hover:underline">
            Забыли пароль? Восстановить на странице входа
          </Link>
        </div>
      )}

      {error !== null && (
        <p role="alert" className="mt-3 text-[13px] font-semibold text-danger">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <Button type="submit" block loading={accepting} disabled={declining}>
          {needsProfile ? "Создать учётную запись" : "Принять"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          block
          loading={declining}
          disabled={accepting}
          onClick={() => void handleDecline()}
        >
          Отклонить
        </Button>
      </div>
    </form>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  if (value === null) return null;
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-faint">{label}</dt>
      <dd className="min-w-0 truncate text-right font-semibold text-fg">{value}</dd>
    </div>
  );
}

const TONES = {
  success: "text-success",
  danger: "text-danger",
  warning: "text-warning",
  neutral: "text-muted",
} as const;

function Outcome({
  icon: Icon,
  tone,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  tone: keyof typeof TONES;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2.5 py-4 text-center">
      <Icon className={`size-9 ${TONES[tone]}`} aria-hidden />
      <h2 className="text-lg font-extrabold text-fg">{title}</h2>
      <p className="text-[13px] text-muted">{description}</p>
      {action !== undefined && <div className="mt-2 w-full">{action}</div>}
    </div>
  );
}
